import React, { useState, useEffect, useMemo } from "react";
import { Button, Col, InputNumber, Modal, Row, Space, Typography } from "antd";
import { CalculatorOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
  getDenominacionesConfig,
  decimalesDeLaDenominacion,
} from "./configs/denominaciones.config";
import { useConfig } from "../../contexts/ConfigContext";
import { formatNumberByCountry } from "../../utils/formatNumbers";

const DenominacionesCalculator = ({
  value = 0,
  onChange,
  modalTitle = "Cálculo de Importe por Denominaciones",
  buttonTitle = "Calcular",
  disabled = false,
  apertura = false,
  placeholder = "Importe calculado",
}) => {
  const { country, loading } = useConfig();

  const [modalVisible, setModalVisible] = useState(false);
  const [denominaciones, setDenominaciones] = useState({});
  const [totalCalculado, setTotalCalculado] = useState(0);

  const config = useMemo(() => {
    return country ? getDenominacionesConfig(country.code) : null;
  }, [country]);

  useEffect(() => {
    if (!config) return;

    // Los valores por defecto (ej. fondo fijo de $99.50) sólo aplican a la apertura de caja.
    // En el cierre siempre debe arrancar en cero.
    if (apertura && config.useDefaultValues) {
      setDenominaciones(config.defaultValues);
      setTotalCalculado(
        Object.entries(config.defaultValues).reduce((sum, [v, c]) => sum + parseFloat(v) * c, 0)
      );
    } else {
      const vacio = {};
      [...config.bills, ...config.coins].forEach((v) => (vacio[v] = 0));
      setDenominaciones(vacio);
      setTotalCalculado(0);
    }
  }, [config, apertura]);

  if (loading || !country) {
    return null;
  }

  const { currencySymbol, bills, coins, defaultValues, expectedDefaultTotal, useDefaultValues } =
    config;

  // Un IMPORTE se escribe como en el resto de la aplicación; una PIEZA, con los
  // decimales que ella misma tenga.
  //
  // No son lo mismo y por eso son dos reglas. Un billete de ₡500 se rotula
  // «₡ 500» --ponerle céntimos a una pieza no significa nada--, pero el total
  // del arqueo es dinero y tiene que leerse igual que el cuadro de caja de al
  // lado: si uno dice «₡ 25.000» y el otro «₡ 25.000,00», el cajero se queda
  // mirando dos números que no sabe si son el mismo.
  //
  // Antes el total también usaba los decimales de las piezas. Era un apaño de
  // cuando Costa Rica declaraba cinco decimales de formato y un billete de 500
  // salía «₡500,00000»; corregido eso en countries.js, el apaño sobraba y
  // empujaba en sentido contrario.
  const enMoneda = (valor, cuantos) => formatNumberByCountry(valor, country, true, cuantos);

  const actualizarDenominacion = (valor, cantidad) => {
    setDenominaciones((prev) => {
      const nuevas = { ...prev, [valor]: cantidad };
      const nuevoTotal = Object.entries(nuevas).reduce((sum, [v, c]) => sum + parseFloat(v) * c, 0);
      setTotalCalculado(nuevoTotal);
      return nuevas;
    });
  };

  const establecerValoresPredeterminados = () => {
    setDenominaciones(defaultValues);
    const total = Object.entries(defaultValues).reduce((sum, [v, c]) => sum + parseFloat(v) * c, 0);
    setTotalCalculado(total);
  };

  const limpiarDenominaciones = () => {
    const vacio = {};
    [...bills, ...coins].forEach((v) => (vacio[v] = 0));
    setDenominaciones(vacio);
    setTotalCalculado(0);
  };

  const abrirModal = () => {
    setModalVisible(true);
  };

  const confirmarDenominaciones = () => {
    const importeRedondeado = Math.round(totalCalculado * 100) / 100;

    if (useDefaultValues && importeRedondeado !== expectedDefaultTotal && apertura) {
      window.messageApi.confirm({
        title: "Confirmación de importe",
        content: (
          <>
            <p>
              <ExclamationCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
              El importe calculado es{" "}
              <strong>{enMoneda(importeRedondeado)}</strong>
            </p>
            <p>
              Este importe no es {enMoneda(expectedDefaultTotal)} como
              se esperaba.
            </p>
            <p>¿Desea continuar con este importe?</p>
          </>
        ),
        width: 500,
        okText: "Aceptar",
        cancelText: "Cancelar",
        onOk: () => procesarConfirmacion(importeRedondeado),
      });
    } else {
      procesarConfirmacion(importeRedondeado);
    }
  };

  const procesarConfirmacion = (importe) => {
    onChange?.(importe);
    window.messageApi?.success(
      `Importe establecido: ${enMoneda(importe)}`
    );
    setModalVisible(false);
  };

  return (
    <>
      <Space.Compact style={{ width: "100%" }}>
        <InputNumber
          style={{ width: "85%" }}
          placeholder={placeholder}
          min={0}
          step={0.01}
          precision={2}
          value={enMoneda(value)}
          disabled
        />
        <Button
          type="primary"
          icon={<CalculatorOutlined />}
          onClick={abrirModal}
          disabled={disabled}
          style={{ width: "15%" }}
          title={buttonTitle}
        />
      </Space.Compact>

      <Modal
        title={modalTitle}
        open={modalVisible}
        onOk={confirmarDenominaciones}
        onCancel={() => setModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setModalVisible(false)}>
            Cancelar
          </Button>,
          <Button key="submit" type="primary" onClick={confirmarDenominaciones}>
            Confirmar Importe
          </Button>,
        ]}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Typography.Title level={5} style={{ margin: 0 }}>
            Ingrese la cantidad de billetes y monedas:
          </Typography.Title>
          <Space>
            {apertura && useDefaultValues && (
              <Button type="default" size="small" onClick={establecerValoresPredeterminados}>
                Valores por defecto ({currencySymbol}
                {expectedDefaultTotal})
              </Button>
            )}
            <Button danger size="small" onClick={limpiarDenominaciones}>
              Limpiar
            </Button>
          </Space>
        </div>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Typography.Title
              level={5}
              style={{
                textAlign: "center",
                marginBottom: 16,
              }}
            >
              Billetes
            </Typography.Title>
            {bills.map((valor) => (
              <Space.Compact key={valor} style={{ width: "100%", marginBottom: 8 }}>
                <Typography.Text
                  style={{
                    minWidth: "110px",
                    textAlign: "right",
                    paddingRight: 8,
                  }}
                >
                  {enMoneda(valor, decimalesDeLaDenominacion(valor))}:
                </Typography.Text>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  value={denominaciones[valor] || 0}
                  onChange={(v) => actualizarDenominacion(valor, v || 0)}
                  placeholder="Cantidad"
                />
              </Space.Compact>
            ))}
          </Col>

          <Col span={12}>
            <Typography.Title
              level={5}
              style={{
                textAlign: "center",
                marginBottom: 16,
              }}
            >
              Monedas
            </Typography.Title>
            {coins.map((valor) => (
              <Space.Compact key={valor} style={{ width: "100%", marginBottom: 8 }}>
                <Typography.Text
                  style={{
                    minWidth: "90px",
                    textAlign: "right",
                    paddingRight: 8,
                  }}
                >
                  {enMoneda(valor, decimalesDeLaDenominacion(valor))}:
                </Typography.Text>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  value={denominaciones[valor] || 0}
                  onChange={(v) => actualizarDenominacion(valor, v || 0)}
                  placeholder="Cantidad"
                />
              </Space.Compact>
            ))}
          </Col>
        </Row>

        <Typography.Title level={4} style={{ marginTop: 24, textAlign: "right" }}>
          Total: {enMoneda(totalCalculado)}
        </Typography.Title>
      </Modal>
    </>
  );
};

export default DenominacionesCalculator;
